/* v3.18-1291 — EmailDraftModal now wraps ComposeAndApprove.
 *
 * External API is unchanged (open / onClose / draft / endpoint /
 * basePayload / workflowLabel / onSent) so every caller keeps working
 * without code changes.  Internally it opens the universal
 * ComposeAndApprove dialog and sends through the caller-provided
 * ``endpoint`` via onCustomSend so the specialized backend routes
 * (activate at-risk, offer send, disclosure blast, etc.) continue to
 * fire with their own audit trails.
 *
 * Previous 200-line bespoke rendering has been retired.
 */
import { useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import ComposeAndApprove from './ComposeAndApprove';

const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, '').trim();

export const EmailDraftModal = ({
  open,
  onClose,
  draft,           // { subject, body, to, cc, template_loaded, template_load_error, ... }
  endpoint,        // POST URL — same one that returned the draft
  basePayload,     // payload that triggered the draft (e.g. {active:true})
  workflowLabel,   // human label for toast/audit
  onSent,          // optional callback after successful send
}) => {
  const d = draft || {};
  const initialBody = useMemo(() => (/<[a-z][\s\S]*>/i.test(d.body || '') ? stripHtml(d.body) : (d.body || '')), [d.body]);

  return (
    <ComposeAndApprove
      open={!!open && !!draft}
      onOpenChange={(v) => { if (!v) onClose(); }}
      initial={{
        template_key: 'blank',
        subject:      d.subject || '',
        body:         initialBody,
        to:           d.to || [],
        cc:           d.cc || [],
        client_id:    d.client_id || null,
      }}
      onCustomSend={async (payload) => {
        if (!d.template_loaded && d.template_load_error) {
          toast.error(`Template failed to load: ${d.template_load_error}`);
          return { __cancel: true };
        }
        try {
          const res = await api.post(endpoint, {
            ...(basePayload || {}),
            override_subject: payload.subject,
            override_body:    payload.body,
            override_to:      payload.to,
            override_cc:      payload.cc,
          });
          toast.success(workflowLabel ? `${workflowLabel} sent.` : 'Email sent.');
          onSent?.(res.data);
          return res.data;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to send.');
          return { __cancel: true };
        }
      }}
    />
  );
};

export default EmailDraftModal;
